import { registerSuperProperties } from '@/lib/mixpanel';

// Real-time transport resolution.
//
// WebSocket (the GraphQL subscriptions) is the default transport everywhere.
// The SSE deal-events channel is opt-in via the `?mode=sse` URL param. Seeing
// `?mode=sse` once **latches** SSE for the rest of the browser-tab session
// (`sessionStorage`), so it survives client-side navigations that drop the
// query param; a fresh tab starts back on WebSocket.
//
// This lives in its own module (not `config.ts`) so the super-property helper's
// Mixpanel import doesn't create a config → mixpanel import cycle.

const SESSION_MODE_KEY = 'dealRealtimeMode';

function readSessionMode(): string | null {
  try {
    return window.sessionStorage.getItem(SESSION_MODE_KEY);
  } catch {
    return null;
  }
}

function latchSessionMode(mode: string): void {
  try {
    window.sessionStorage.setItem(SESSION_MODE_KEY, mode);
  } catch {
    // sessionStorage unavailable (private mode, quota, …) — the URL param still
    // drives the current view, it just won't stick across navigations.
  }
}

export function isDealEventsSseEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  const urlMode = new URLSearchParams(window.location.search).get('mode');
  if (urlMode === 'sse') {
    latchSessionMode('sse');
    return true;
  }
  return readSessionMode() === 'sse';
}

export type DealRealtimeTransport = 'sse' | 'ws';

export function dealRealtimeTransport(): DealRealtimeTransport {
  return isDealEventsSseEnabled() ? 'sse' : 'ws';
}

/**
 * Register the resolved real-time transport as a Mixpanel super property so
 * every event (SSE/WS lifecycle and all others) can be segmented by which
 * transport the user is on. No-op until Mixpanel is initialized.
 */
export function registerDealRealtimeTransportProperty(): void {
  registerSuperProperties({ dealRealtimeTransport: dealRealtimeTransport() });
}
